'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Star } from 'lucide-react';
import { Product } from '@/types';
import { useCartStore } from '@/store';
import { useCurrency } from '@/hooks/useCurrency';
import styles from './ProductCard.module.css';

interface ProductCardProps {
    product: Product;
    index?: number;
}

export default function ProductCard({ product, index = 0 }: ProductCardProps) {
    const { addItem, openCart } = useCartStore();
    const { format } = useCurrency();
    const [imgError, setImgError] = useState(false);

    const handleAddToCart = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!product.isAvailable) return;
        addItem(product);
        openCart();
    };

    // Generate intensity bars (partner products have no intensity)
    const intensity = product.intensity;
    const intensityBars = intensity === null
        ? null
        : Array.from({ length: 10 }, (_, i) => (
            <span
                key={i}
                className={`${styles.intensityBar} ${i < intensity ? styles.active : ''}`}
            />
        ));

    return (
        <motion.article
            className={styles.card}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            whileHover={{ y: -8 }}
        >
            <Link href={`/products/${product.slug}`} className={styles.link}>
                {/* Image Container */}
                <div className={styles.imageContainer}>
                    {/* Gradient overlay */}
                    <div className={styles.imageOverlay} />

                    {/* Product image */}
                    {product.imageUrl && !imgError ? (
                        <div className={styles.imageStage}>
                            <Image
                                src={product.imageUrl}
                                alt={product.name}
                                fill
                                sizes="(max-width: 380px) 100vw, (max-width: 600px) 50vw, (max-width: 900px) 50vw, (max-width: 1200px) 33vw, 25vw"
                                className={styles.productImage}
                                style={{ objectFit: 'cover' }}
                                priority={index < 2}
                                onError={() => setImgError(true)}
                            />
                        </div>
                    ) : (
                        <div className={styles.imagePlaceholder}>
                            <div className={styles.capsuleIcon}>
                                <svg viewBox="0 0 60 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <ellipse cx="30" cy="85" rx="25" ry="10" fill="rgba(201, 164, 92, 0.3)" />
                                    <rect x="10" y="20" width="40" height="50" rx="5" fill="url(#grad1)" />
                                    <rect x="12" y="5" width="36" height="20" rx="3" fill="url(#grad2)" />
                                    <ellipse cx="30" cy="70" rx="20" ry="5" fill="rgba(0,0,0,0.2)" />
                                    <defs>
                                        <linearGradient id="grad1" x1="10" y1="20" x2="50" y2="70" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#D4AF37" />
                                            <stop offset="1" stopColor="#8B6914" />
                                        </linearGradient>
                                        <linearGradient id="grad2" x1="12" y1="5" x2="48" y2="25" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#C9A45C" />
                                            <stop offset="1" stopColor="#6B5A3E" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </div>
                        </div>
                    )}

                    {/* Featured badge */}
                    {product.isFeatured && (
                        <span className={styles.badge}>
                            <Star size={12} />
                            Featured
                        </span>
                    )}

                    {/* Quick add container */}
                    <div className={styles.actionContainer}>
                        <motion.button
                            className={styles.quickAdd}
                            onClick={handleAddToCart}
                            disabled={!product.isAvailable}
                            aria-label={product.isAvailable ? `Add ${product.name} to cart` : `${product.name} is out of stock`}
                            whileHover={product.isAvailable ? { scale: 1.05 } : undefined}
                            whileTap={product.isAvailable ? { scale: 0.95 } : undefined}
                        >
                            <ShoppingBag size={18} />
                            {product.isAvailable ? 'Add to Cart' : 'Out of stock'}
                        </motion.button>
                    </div>
                </div>

                {/* Content */}
                <div className={styles.content}>
                    {/* Category + origin meta */}
                    {(product.category || product.origin) && (
                        <div className={styles.metaRow}>
                            {product.category && <span className={styles.category}>{product.category}</span>}
                            {product.origin && <span className={styles.origin}>{product.origin}</span>}
                        </div>
                    )}

                    {/* Name */}
                    <h3 className={styles.name}>{product.name}</h3>

                    {/* Description */}
                    <p className={styles.description}>{product.description}</p>

                    {/* Intensity meter */}
                    {intensity !== null && (
                        <div className={styles.intensityContainer}>
                            <span className={styles.intensityLabel}>Intensity</span>
                            <div className={styles.intensityMeter}>
                                {intensityBars}
                            </div>
                            <span className={styles.intensityValue}>{intensity}</span>
                        </div>
                    )}

                    {/* Footer */}
                    <div className={styles.footer}>
                        <span className={styles.price}>{format(product.price)}</span>
                        <button
                            className={styles.footerAdd}
                            onClick={handleAddToCart}
                            disabled={!product.isAvailable}
                            aria-label={product.isAvailable ? `Add ${product.name} to cart` : `${product.name} is out of stock`}
                        >
                            <ShoppingBag size={18} />
                            <span className={styles.footerAddLabel}>{product.isAvailable ? 'Add' : 'Out of stock'}</span>
                        </button>
                    </div>
                </div>
            </Link>
        </motion.article>
    );
}
